import React from 'react';
import { connect } from 'react-redux';
import _filter from 'lodash/filter';
import _map from 'lodash/map';
import _max from 'lodash/max';
import _reduce from 'lodash/reduce';
import _reject from 'lodash/reject';
import _size from 'lodash/size';
import PropTypes from 'prop-types';
// Models
import {
  periodToEvent, periodToPeriodInDay, eventToPeriod, eventStartsInWeekend, eventStartsInDay, sanitizeEvents,
} from 'models/planning';
// Custom
import { openDialog } from 'actions';
import { dateGetter, dateGenerator, dateQuery } from 'utils/DateUtils';
import { DuplicateTextButton, EmptyWeekendTextButton } from 'components/buttons/TextButtons';
import WeekCalendar from './WeekCalendar';

class PlanningWeekEdit extends React.Component {
  constructor(props) {
    super(props);

    this.handleDuplicateMonday = this.handleDuplicateMonday.bind(this);
    this.handleEmptyWeekend = this.handleEmptyWeekend.bind(this);
    this.handleSelectEvent = this.handleSelectEvent.bind(this);
    this.handleNewEvent = this.handleNewEvent.bind(this);
    this.handleValidateEditEvent = this.handleValidateEditEvent.bind(this);
    this.handleDeleteEvent = this.handleDeleteEvent.bind(this);
    this.handleEventChange = this.handleEventChange.bind(this);

    this.editEventId = null;
  }

  // Handlers
  handleDuplicateMonday() {
    const { events } = this.props;

    // Extract monday events
    const mondayEvents = _filter(events, (event) => eventStartsInDay(event, 0));
    // Now apply to other days
    const newEvents = _reduce(mondayEvents, (result, event) => {
      // Transform this event in period
      const period = eventToPeriod(event);
      // Create a new slot for all days
      for (let i = 0; i < 7; i += 1) {
        const periodForDay = periodToPeriodInDay(period, i);
        // Transform the new period in event
        const eventForDay = periodToEvent(periodForDay);
        result.push({
          ...eventForDay,
          id: _size(result) + 1,
        });
      }
      return result;
    }, []);

    this.doHandleUpdate(newEvents);
  }

  handleEmptyWeekend() {
    const { events } = this.props;

    // Reject from events the ones that start in weekend
    const newEvents = _reject(events, (event) => eventStartsInWeekend(event));

    this.doHandleUpdate(newEvents);
  }

  handleSelectEvent(event) {
    const { editable, handleOpenDialog } = this.props;
    if (editable) {
      this.editEventId = event.id;
      // Open popup to edit period
      handleOpenDialog({
        title: {
          id: 'title.editPeriod',
        },
        actions: [
          { type: 'cancel' },
          { type: 'validate' },
          { type: 'delete' },
        ],
        component: {
          type: 'PLANNING_PERIOD_EDIT',
          props: {
            period: eventToPeriod(event),
            onValidate: this.handleValidateEditEvent,
            onDelete: this.handleDeleteEvent,
          },
        },
      });
    }
  }

  handleValidateEditEvent({ start, end }) {
    const { events } = this.props;

    // Update the concerned event
    const newEvents = _map(events, (existingEvent) => ((existingEvent.id === this.editEventId) ? {
      ...existingEvent, start, end,
    } : existingEvent));

    this.doHandleUpdate(newEvents);
  }

  handleDeleteEvent() {
    const { events } = this.props;

    // Remove concerned event
    this.doHandleUpdate(_reject(events, ['id', this.editEventId]));
  }

  handleNewEvent({ start, end }) {
    const { editable, events } = this.props;
    if (editable && dateQuery.isValid(start) && dateQuery.isValid(end)) {
      // Remove seconds
      const usedStart = dateGenerator.date(start, { seconds: 0 });
      const usedEnd = dateGenerator.date(end, { seconds: 0 });
      // Create an array with new event
      const id = _size(events) > 0 ? _max(_map(events, 'id')) + 1 : 1;
      const newEvents = [...events, { id, start: usedStart, end: usedEnd }];

      this.doHandleUpdate(newEvents);
    }
  }

  handleEventChange({ event, start, end }) {
    const { editable, events } = this.props;
    if (editable && dateQuery.isValid(start) && dateQuery.isValid(end)) {
      // Remove seconds
      const usedStart = dateGenerator.date(start, { seconds: 0 });
      const usedEnd = dateGenerator.date(end, { seconds: 0 });
      // We allow only to resize / move an event on the same day
      let computedEnd = usedEnd;
      const startDay = dateGetter.day(usedStart);
      const endDay = dateGetter.day(usedEnd);
      if (endDay !== startDay) {
        computedEnd = dateGenerator.date(usedStart, { hours: 23, minutes: 59 });
      }
      // Update the concerned event
      const newEvents = _map(events, (existingEvent) => ((existingEvent.id === event.id) ? {
        ...existingEvent, start: usedStart, end: computedEnd,
      } : existingEvent));

      this.doHandleUpdate(newEvents);
    }
  }

  // Private
  doHandleUpdate(events) {
    const { onChange } = this.props;
    onChange(sanitizeEvents(events));
  }

  render() {
    const { editable, completeMode, events } = this.props;

    return (
      <div>
        {completeMode && (
          <div className="row py-3">
            <div className="col-12 d-flex left-spaced-buttons">
              <DuplicateTextButton
                size="small"
                labelId="button.duplicateMonday"
                onClick={this.handleDuplicateMonday}
              />
              <EmptyWeekendTextButton
                size="small"
                onClick={this.handleEmptyWeekend}
              />
            </div>
          </div>
        )}
        <WeekCalendar
          selectable={editable}
          events={events}
          onSelectEvent={this.handleSelectEvent}
          onSelectSlot={this.handleNewEvent}
          onEventDrop={this.handleEventChange}
          onEventResize={this.handleEventChange}
        />
      </div>
    );
  }
}

PlanningWeekEdit.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      start: PropTypes.instanceOf(Date).isRequired,
      end: PropTypes.instanceOf(Date).isRequired,
    }),
  ).isRequired,
  editable: PropTypes.bool,
  completeMode: PropTypes.bool,
  onChange: PropTypes.func,
  handleOpenDialog: PropTypes.func.isRequired,
};

PlanningWeekEdit.defaultProps = {
  editable: true,
  completeMode: false,
  onChange: null,
};

const mapDispatchToProps = (dispatch) => ({
  handleOpenDialog: (props) => {
    dispatch(openDialog(props));
  },
});

module.exports = connect(
  null,
  mapDispatchToProps,
)(PlanningWeekEdit);



// WEBPACK FOOTER //
// ./components/plannings/PlanningWeekEdit.js